import type { WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppFormState } from '@shared/ui/fields'

import { useListsStore } from '@shared/model'
import { useToast } from '@shared/ui/toast'

import { DisciplineTutorChangeReason } from './enums'
import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineScheduleContext } from './discipline-schedule-context.hook'
import { SCHEDULE_COMMENT_MAX_LENGTH } from './discipline-schedule'

export interface DisciplineTutorReasonSelectOption extends AppSelectOption<string> {
  description: string
}

interface ModalDisciplineTutorChangeReasonReturn {
  refTitle: ComputedRef<string>
  refKidName: ComputedRef<string>
  refTutorName: ComputedRef<string>
  refTutorUrl: ComputedRef<string | null>
  refReasonOptions: ComputedRef<DisciplineTutorReasonSelectOption[]>
  refReason: WritableComputedRef<string | undefined>
  refReasonState: ComputedRef<AppFormState>
  refSubReasonOptions: ComputedRef<AppSelectOption<string>[]>
  refSubReasons: WritableComputedRef<string[]>
  refComment: ComputedRef<string>
  setComment: (event: { value: string }) => void
  close: () => void
  confirm: () => void
}

/** The first Change tutor step: why the tutor goes. Only "tutor fit issue" has performance signals. */
export const useModalDisciplineTutorChangeReason = (): ModalDisciplineTutorChangeReasonReturn => {
  const toast = useToast()
  const store = useDisciplineScheduleStore()
  const listsStore = useListsStore()
  const { refDisciplineLabel, refKidName, refTutorName, refTutorUrl, close } = useDisciplineScheduleContext()

  const refTitle = computed<string>(() => `Change ${refDisciplineLabel.value} tutor for kid ${refKidName.value}`)

  const refReasonOptions = computed<DisciplineTutorReasonSelectOption[]>(() => {
    const reasons = listsStore.getLists?.discipline.change_tutor_reason

    return Object.entries(reasons?.title ?? {}).map(([value, text]) => ({
      value,
      text,
      description: reasons?.description[value] ?? '',
    }))
  })

  const refReason = computed<string | undefined>({
    get: () => store.getTutorReason ?? undefined,
    set: (value) => {
      store.setTutorReason((value as DisciplineTutorChangeReason | undefined) ?? null)
    },
  })

  const refReasonState = computed<AppFormState>(() => (store.hasReasonError ? 'error' : 'static'))

  const refSubReasonOptions = computed<AppSelectOption<string>[]>(() => {
    const reason = store.getTutorReason
    if (reason !== DisciplineTutorChangeReason.TutorFitIssue) return []

    const subReasons = listsStore.getLists?.discipline.change_tutor_sub_reason[reason] ?? {}
    return Object.entries(subReasons).map(([value, text]) => ({ value, text }))
  })

  const refSubReasons = computed<string[]>({
    get: () => store.getTutorSubReasons,
    set: (value) => {
      store.setTutorSubReasons(value)
    },
  })

  const refComment = computed<string>(() => store.getTutorComment)

  const setComment = (event: { value: string }): void => {
    store.setTutorComment(event.value.slice(0, SCHEDULE_COMMENT_MAX_LENGTH))
  }

  const confirm = (): void => {
    if (!store.getTutorReason) {
      store.setReasonError(true)
      toast.warning({ title: 'Warning!', description: 'The field reason is required.' })
      return
    }

    store.nextStep()
  }

  return {
    refTitle,
    refKidName,
    refTutorName,
    refTutorUrl,
    refReasonOptions,
    refReason,
    refReasonState,
    refSubReasonOptions,
    refSubReasons,
    refComment,
    setComment,
    close,
    confirm,
  }
}
